// React & core
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// UI Components
import { Button } from '@/common-utils/components/ui/button';
import { Separator } from '@/common-utils/components/ui/separator';

// Renders/Helpers
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';

// Enums & Namespaces
import ICONS from '@/common-utils/enums/icons';
import { nsCustomization } from '@/app/i18n/namespaces';

// Module Components
import BeneficiaryAccordion from '../../../beneficiaries/components/BeneficiaryAccordion';
import ModalAddBeneficiary from '../modals/addBeneficiary/ModalAddBeneficiary';

// Store
import useRootOrganizationStore from '@/app/store/rootOrganization';
import compareArraysOfObjects from '@/common-utils/functions/compareArraysOfObjects';
import customizationFields from '../../fields/customizationFields';
import CUSTOMIZATION_URL_ACTIONS from '../../enums/CustomizationUrlActions';
import CheckboxHelper from '@/common-utils/components/renders/checkbox/CheckboxHelper';
import { Skeleton } from '@/common-utils/components/ui/skeleton';


/**
 *
 * @param root0
 * @param root0.customization
 * @param root0.isPackage
 * @param root0.beneficiaries
 * @param root0.setBeneficiaries
 * @param root0.setIsDirtyMoneticSettings
 * @param root0.checkedMpfOptions
 * @param root0.setCheckedMpfOptions
 * @param root0.isForcingGatewayOn
 * @param root0.setIsValidMoneticSettings
 * @param root0.action
 * @param root0.isLoading
 * @returns {JSX} MoneticSettings
 */
function MoneticSettings({
    customization,
    isPackage,
    beneficiaries,
    setBeneficiaries,
    setIsDirtyMoneticSettings,
    checkedMpfOptions,
    setCheckedMpfOptions,
    isForcingGatewayOn,
    setIsValidMoneticSettings,
    action,
    isLoading = false
}) {

    // Constants
    const ENTITY_PREFIX = 'details.monetic_settings';
    const isDisabled = action === CUSTOMIZATION_URL_ACTIONS.SHOW || (isPackage && action !== CUSTOMIZATION_URL_ACTIONS.DUPLICATE);

    // Store
    const rootOrganization = useRootOrganizationStore(state => state.rootOrganization);

    // Dynamic Constants
    const mainConnectionPoint = rootOrganization?.connectionPoints?.find(cp => cp.instanceType === 'MAIN');
    const backupConnectionPoint = rootOrganization?.connectionPoints?.find(cp => cp.instanceType === 'BACKUP');

    // State
    const [checkedOption, setCheckedOption] = useState(isForcingGatewayOn);
    const [openItems, setOpenItems] = useState([]);
    const [showModalAddBeneficiary, setShowModalAddBeneficiary] = useState(false);
    const [isAutomateMode, setIsAutomateMode] = useState(false);

    // Hooks
    useEffect(() => {
        // Initialize the checked option with the forcing gateway option
        setCheckedOption(isForcingGatewayOn);
    }, [isForcingGatewayOn]);

    useEffect(() => {
        // Check if the automate mode is selected
        setIsAutomateMode(checkedMpfOptions.some((item) => item.name === customizationFields.MPF_OPTIONS.AUTOMATE_MODE.key));
    }, [checkedMpfOptions]);

    useEffect(() => {
        // Looks for monetic settings changes
        if (beneficiaries && customization?.beneficiaries) {
            const hasSameBeneficiaries = compareArraysOfObjects(beneficiaries, customization?.beneficiaries, 'uuid');
            if (!hasSameBeneficiaries || checkedOption !== isForcingGatewayOn) {
                setIsDirtyMoneticSettings(true);
            } else {
                setIsDirtyMoneticSettings(false);
            }
        }

        // Looks for monetic settings validity
        if (isAutomateMode && beneficiaries?.length > 1) {
            setIsValidMoneticSettings(false);
        } else {
            setIsValidMoneticSettings(true);
        }
    }, [checkedOption, beneficiaries, customization, isAutomateMode, isForcingGatewayOn]);

    useEffect(() => {
        // Updates the checked MPF options when the forcing gateway option is changed
        if (checkedOption) {
            setCheckedMpfOptions([...checkedMpfOptions, { name: customizationFields.MPF_OPTIONS.FORCING_GATEWAY.key, value: null }]);
        } else {
            setCheckedMpfOptions(checkedMpfOptions.filter((item) => item.name !== customizationFields.MPF_OPTIONS.FORCING_GATEWAY.key));
        }
    }, [checkedOption]);

    return (
        <>
            <div>
                <div className='p-4 space-y-4'>
                    <div className='flex items-center gap-3'>
                        <CheckboxHelper
                            isChecked={checkedOption}
                            onCheckedChange={() => setCheckedOption(!checkedOption)}
                            isDisabled={isDisabled || isLoading}
                            entity={`${ENTITY_PREFIX}.gateway.forcing_gateway`}
                            namespace={nsCustomization}
                        />
                        <TooltipHelper
                            trigger={<Icon icon={ICONS.HELP} />}
                            content={<Entity entity={`${ENTITY_PREFIX}.gateway.tooltip`} namespace={nsCustomization} />}
                        />
                    </div>
                    <div className='flex flex-row gap-8'>
                        <div className='flex flex-col items-start gap-2'>
                            <div className='flex flex-row gap-2'>
                                <Entity entity={`${ENTITY_PREFIX}.gateway.address`} namespace={nsCustomization} />
                                {mainConnectionPoint ?
                                    <span className='font-bold'>{mainConnectionPoint?.ipAddress}</span>
                                    :
                                    <Entity entity={`${ENTITY_PREFIX}.gateway.none`} namespace={nsCustomization} className='font-bold' />
                                }
                            </div>
                            {mainConnectionPoint &&
                                <div className='flex flex-row gap-2'>
                                    <Entity entity={`${ENTITY_PREFIX}.gateway.port`} namespace={nsCustomization} />
                                    <span className='font-bold'>{mainConnectionPoint?.port}</span>
                                </div>
                            }
                        </div>
                        <div className='flex flex-row items-start gap-2'>
                            <Entity entity={`${ENTITY_PREFIX}.gateway.backup`} namespace={nsCustomization} />
                            {backupConnectionPoint ?
                                <div className='flex flex-col items-start gap-3'>
                                    <div className='flex flex-row gap-2'>
                                        <Entity entity={`${ENTITY_PREFIX}.gateway.address`} namespace={nsCustomization} />
                                        <span className='font-bold'>{backupConnectionPoint?.ipAddress}</span>
                                    </div>
                                    <div className='flex flex-row gap-2'>
                                        <Entity entity={`${ENTITY_PREFIX}.gateway.port`} namespace={nsCustomization} />
                                        <span className='font-bold'>{backupConnectionPoint?.port}</span>
                                    </div>
                                </div>
                                :
                                <Entity entity={`${ENTITY_PREFIX}.gateway.none`} namespace={nsCustomization} className='font-bold' />
                            }
                        </div>
                    </div>
                </div>
                <h5>
                    <Entity entity={`${ENTITY_PREFIX}.beneficiaries.title`} namespace={nsCustomization} />
                </h5>
                <Separator className='my-2' />
                <div className='mb-2'>
                    <Button
                        variant='secondary'
                        icon={ICONS.LINK}
                        entity={`${ENTITY_PREFIX}.beneficiaries.add`}
                        namespace={nsCustomization}
                        onClick={() => setShowModalAddBeneficiary(true)}
                        className={(isDisabled || isLoading) ? 'hidden' : 'h-8 rounded-lg pl-1 pr-2'}
                    />
                </div>
                {
                    isLoading ?
                        <div className='flex flex-col gap-2'>
                            <Skeleton className='h-12 w-full bg-gray-200' />
                        </div>
                        :
                        <BeneficiaryAccordion
                            beneficiaries={beneficiaries ?? []}
                            openItems={openItems}
                            setOpenItems={setOpenItems}
                            showActions={false}
                        />
                }
            </div>
            {
                showModalAddBeneficiary &&
                <ModalAddBeneficiary
                    isOpen={showModalAddBeneficiary}
                    setIsOpen={setShowModalAddBeneficiary}
                    contractAccessId={customization?.contractAccessUuid}
                    beneficiaries={beneficiaries ?? []}
                    setBeneficiaries={setBeneficiaries}
                />
            }
        </>
    );
}

MoneticSettings.propTypes = {
    customization: PropTypes.object,
    isPackage: PropTypes.bool.isRequired,
    beneficiaries: PropTypes.array.isRequired,
    setBeneficiaries: PropTypes.func.isRequired,
    setIsDirtyMoneticSettings: PropTypes.func.isRequired,
    setIsValidMoneticSettings: PropTypes.func.isRequired,
    checkedMpfOptions: PropTypes.array.isRequired,
    setCheckedMpfOptions: PropTypes.func.isRequired,
    action: PropTypes.string,
    isForcingGatewayOn: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool
};

export default MoneticSettings;